import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { articleCrumbs, breadcrumbJsonLd, getTopic } from '../articles';
import { Breadcrumb, FitCheckBox, MoreTopics, QuickLink } from '../components/articles/ArticleParts';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';
import '../components/svs-guide/svs-guide.css';

// V1 of 17 planned knowledge-base topics (see the "Content-Seiten pstax.at"
// doc) — reference implementation other topics will copy, so this follows
// SvsSozialversicherungGuide.tsx / SvsSocialInsuranceGuide.tsx's structure
// as closely as the content allows. T1 (PublicOnly/InternalOnly audience
// switch), T2 (page registry), T3 (ClickFlow engine) and T4 (a generic
// GuideArticle template) aren't built yet — this page is hand-wired the same
// way the existing guides are, same as they are.
// Published article (src/articles.ts) — prerenderRoutes.ts emits the
// matching robots tag for the static HTML.
const INDEXABLE = true;

const TOPIC = getTopic('vat');
const ARTICLE = TOPIC.de;
const CRUMBS = articleCrumbs(TOPIC, 'de');
const PATH = ARTICLE.path;
const CANONICAL = `https://pstax.at${PATH}`;
export const TITLE_UST_DE = ARTICLE.title;
export const DESCRIPTION_UST_DE = ARTICLE.description;
const H1 = ARTICLE.h1;

const AUTHOR_PERSON = {
  '@type': 'Person',
  name: 'Pascal Schraml',
  jobTitle: 'Steuerberater',
  worksFor: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
};

export const ARTICLE_JSONLD_UST_DE = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: H1,
  datePublished: ARTICLE.datePublished,
  dateModified: ARTICLE.dateModified,
  inLanguage: 'de',
  author: AUTHOR_PERSON,
  reviewedBy: AUTHOR_PERSON,
  publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
  about: 'Umsatzsteuer für Selbstständige in Österreich',
};

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'Die Umsatzsteuer ist nie dein Geld', id: 'nie-dein-geld' },
  { label: 'Wie hoch ist die Umsatzsteuer in Österreich?', id: 'satz' },
  { label: 'Der Vorsteuerabzug: dein Ausgleich fürs Einkaufen', id: 'vorsteuerabzug' },
  { label: 'Welche Rechnung du für den Vorsteuerabzug brauchst', id: 'rechnung' },
  { label: 'Wie oft die Umsatzsteuervoranmeldung (UVA) fällig ist', id: 'uva' },
  { label: 'Musst du überhaupt Umsatzsteuer verrechnen?', id: 'kleinunternehmer' },
  { label: 'Kunden im EU-Ausland', id: 'eu-kunden' },
  { label: 'Wann sich die Umsatzsteuerpflicht freiwillig lohnt', id: 'freiwillig' },
  { label: 'Fehler, die wir am häufigsten sehen', id: 'fehler' },
  { label: 'Häufige Fragen', id: 'faq' },
  { label: 'Quellen', id: 'quellen' },
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating the answers as a second copy of the text.
const plainFaq = (q: string, aPlain: string) => ({ q, aPlain, a: <>{aPlain}</> });

export const FAQS_UST_DE: { q: string; a: React.ReactNode; aPlain: string }[] = [
  plainFaq(
    'Muss ich als Freelancer Umsatzsteuer verlangen?',
    'Nur wenn du nicht die Kleinunternehmerregelung nutzt (bis 55.000 € Jahresumsatz) oder freiwillig darauf verzichtet hast.',
    // F-V2, F-V6
  ),
  plainFaq(
    'Was passiert, wenn ich vergessen habe, Umsatzsteuer zu verrechnen?',
    'Du schuldest sie trotzdem dem Finanzamt – nachträglich beim Kunden draufzurechnen ist oft schwierig, dann fehlt dir das Geld aus eigener Tasche.',
  ),
  plainFaq(
    'Bekomme ich die Mehrwertsteuer auf meinen Laptop zurück?',
    'Ja, als Vorsteuer, wenn du nicht Kleinunternehmer bist und eine ordnungsgemäße Rechnung hast.',
    // F-V7
  ),
  plainFaq(
    'Ist Mehrwertsteuer dasselbe wie Umsatzsteuer?',
    'Ja, umgangssprachlich dasselbe; im Gesetz heißt sie Umsatzsteuer (USt).',
  ),
  plainFaq(
    'Bis wann muss die UVA abgegeben werden?',
    'Am 15. des zweitfolgenden Monats – bei vierteljährlicher Abgabe z. B. 15. Februar, Mai, August, November.',
    // F-V11
  ),
  plainFaq(
    'Muss ich bei Rechnungen ins Ausland Umsatzsteuer verrechnen?',
    'Bei B2B-Leistungen an Unternehmen in der EU meist nicht (Reverse Charge) – dafür brauchst du in der Regel eine UID-Nummer.',
    // F-V8, F-V9
  ),
  plainFaq(
    'Was passiert, wenn ich die Kleinunternehmergrenze überschreite?',
    'Bis 60.500 € läuft die Befreiung noch bis Jahresende, darüber endet sie sofort – ab der Transaktion, die die Grenze reißt.',
    // F-V3
  ),
  plainFaq(
    'Brauche ich eine UID-Nummer als Kleinunternehmer?',
    'Nur wenn du mit Unternehmen in anderen EU-Ländern Geschäfte machst; sie ändert nichts an deinem steuerfreien Status.',
    // F-V8
  ),
  plainFaq(
    'Kann ich freiwillig auf die Kleinunternehmerregelung verzichten?',
    'Ja, mit Formular U12 – das bindet dich dann aber mindestens 5 Kalenderjahre.',
    // F-V6
  ),
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a href={href} target="_blank" rel="noopener" className={className}>
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, öffnet in neuem Tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Bald
  </span>
);

export const UmsatzsteuerGrundlagenGuide: React.FC = () => {
  useSeoHead({
    title: TITLE_UST_DE,
    description: DESCRIPTION_UST_DE,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'de',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/vat-basics-austria/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/umsatzsteuer-grundlagen/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/vat-basics-austria/' },
    ],
    jsonLd: [
      ARTICLE_JSONLD_UST_DE,
      breadcrumbJsonLd(CRUMBS, PATH),
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_UST_DE.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="svs-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <Breadcrumb crumbs={CRUMBS} lang="de" />

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Artikel · Umsatzsteuer
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-4 leading-tight">
              {H1}
            </h1>
            <QuickLink topic={TOPIC} lang="de" />
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Du hörst von Kolleg:innen völlig widersprüchliche Dinge: Der eine verrechnet nie Umsatzsteuer, die
              andere holt sich jedes Jahr tausende Euro Vorsteuer zurück. Beides kann richtig sein – aber nur, wenn
              du weißt, in welcher Situation du gerade bist.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </picture>
              <p className="text-sm text-pstax-gray">
                Stand: <time dateTime="2026-11-02">November 2026</time> · Geprüft von{' '}
                <a href="#autor" className="font-semibold text-pstax-navy hover:text-pstax-link">
                  Pascal Schraml
                </a>
                , Steuerberater, Gründer von PSTax
              </p>
            </div>
            <GuideLanguageSwitch from={PATH} targetLang="EN" label="This page in English →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">Auf einen Blick</h2>
              <ul className="space-y-2">
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Standard-Umsatzsteuersatz: 20 %{/* F-V1 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Kleinunternehmergrenze: 55.000 € brutto/Jahr, 2025 und 2026 unverändert{/* F-V2 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Toleranzgrenze bis 60.500 €: Befreiung läuft bis Jahresende weiter, danach endet sie sofort{/* F-V3 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Vorsteuerabzug nur mit ordnungsgemäßer Rechnung, Kleinbetragsrechnung bis 400 € brutto{/* F-V7 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    UVA: monatlich ab 100.000 € Vorjahresumsatz, sonst vierteljährlich, unter 55.000 € meist gar
                    nicht{/* F-V11 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Freiwilliger Verzicht auf den Kleinunternehmer-Status bindet dich 5 Jahre{/* F-V6 */}
                  </span>
                </li>
              </ul>
            </div>

            <p className="mb-10 leading-relaxed text-pstax-gray">
              Passt die Kleinunternehmerregelung zu dir? → Kleinunternehmer-Guide
              <SoonBadge />
            </p>

            {/* Table of contents */}
            <nav className="toc-top mb-12 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8" aria-label="Auf dieser Seite">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">
                Auf dieser Seite
              </p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Die Umsatzsteuer ist nie dein Geld */}
            <h2 id="nie-dein-geld" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Die Umsatzsteuer ist nie dein Geld
            </h2>
            <p className="mb-4 leading-relaxed">
              Du schlägst sie auf deine Rechnung auf, kassierst sie von deinem Kunden und leitest sie ans Finanzamt
              weiter. Wirtschaftlich trägt sie damit letztlich dein Kunde, nicht du.
            </p>
            <p className="mb-4 leading-relaxed">
              Beispiel: Du stellst 1.000 € netto + 200 € USt in Rechnung, dein Business-Kunde zahlt 1.200 € brutto.
              Du führst die 200 € ans Finanzamt ab, dein Kunde bekommt sie als Vorsteuer zurück. Netto-Effekt für
              einen Business-Kunden: 0 €. Wirklich zahlen tun am Ende nur private Endverbraucher, die nichts
              zurückbekommen.
            </p>
            <Callout tone="orange" label="Klingt einfach, aber">
              Eine fehlerhafte Rechnung kann deinem Kunden den Vorsteuerabzug kosten – und dir im schlimmsten Fall
              USt einbrocken, die du nie verrechnet hast.
            </Callout>

            {/* Wie hoch ist die Umsatzsteuer */}
            <h2 id="satz" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wie hoch ist die Umsatzsteuer in Österreich?
            </h2>
            <p className="mb-8 leading-relaxed">
              Der Regelsteuersatz liegt bei 20 %, für bestimmte Leistungen gelten ermäßigte Sätze von 10 % oder 13
              %{/* F-V1 */}. Das gilt für 2025 und 2026 unverändert; ab 1. Juli 2026 kommt zusätzlich ein Satz von
              4,9 % auf ausgewählte Grundnahrungsmittel dazu – für die meisten Freelancer-Leistungen nicht relevant.
            </p>

            {/* Vorsteuerabzug */}
            <h2 id="vorsteuerabzug" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Der Vorsteuerabzug: dein Ausgleich fürs Einkaufen
            </h2>
            <p className="mb-4 leading-relaxed">
              Die Umsatzsteuer, die du selbst beim Einkauf zahlst, bekommst du als Vorsteuer vom Finanzamt zurück –
              du zahlst effektiv nur die Differenz zwischen verrechneter USt und bezahlter Vorsteuer{/* F-V7 */}.
            </p>
            <p className="mb-4 leading-relaxed">
              Beispiel: Du kaufst einen Laptop um 1.200 € brutto (inkl. 200 € USt) und bekommst die 200 € zurück.
            </p>
            <Callout tone="orange" label="Klingt einfach, aber">
              Ohne ordnungsgemäße Rechnung gibt es keinen Abzug, und auf rein privat veranlasste Käufe nie.
            </Callout>

            {/* Rechnung */}
            <h2 id="rechnung" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Welche Rechnung du für den Vorsteuerabzug brauchst
            </h2>
            <p className="mb-4 leading-relaxed">
              Für den vollen Vorsteuerabzug brauchst du eine Rechnung mit allen gesetzlichen Pflichtangaben; bis 400
              € brutto reicht eine vereinfachte Kleinbetragsrechnung{/* F-V7 */}.
            </p>
            <p className="mb-8 leading-relaxed">
              Was heißt das für dich: Prüf Eingangsrechnungen auf Vollständigkeit, bevor du sie ablegst – eine
              fehlende Angabe kostet im Ernstfall den ganzen Vorsteuerabzug, nicht nur einen Teil.
            </p>

            {/* UVA */}
            <h2 id="uva" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wie oft die Umsatzsteuervoranmeldung (UVA) fällig ist
            </h2>
            <p className="mb-8 leading-relaxed">
              Das hängt von deinem Vorjahresumsatz ab: über 100.000 € monatlich, bis 100.000 € vierteljährlich,
              unter 55.000 € meist gar nicht – die Zahllast bleibt trotzdem fällig{/* F-V11 */}. Fällig ist die UVA
              am 15. des zweitfolgenden Monats, bei vierteljährlicher Abgabe also 15. Februar, Mai, August, November.
            </p>

            {/* Kleinunternehmer */}
            <h2 id="kleinunternehmer" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Musst du überhaupt Umsatzsteuer verrechnen?
            </h2>
            <p className="mb-8 leading-relaxed">
              Nicht zwingend: Als Kleinunternehmer bis 55.000 € Jahresumsatz kannst du von der Umsatzsteuer befreit
              bleiben{/* F-V2 */} – dafür bekommst du dann aber auch keine Vorsteuer zurück{/* F-V5 */}. Welche
              Variante sich für dich lohnt, hängt vor allem von deinen Kunden und Ausgaben ab (mehr dazu im Guide
              zur Kleinunternehmerregelung
              <SoonBadge />).
            </p>

            {/* EU-Kunden */}
            <h2 id="eu-kunden" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Kunden im EU-Ausland
            </h2>
            <p className="mb-8 leading-relaxed">
              Erbringst du Dienstleistungen für ein Unternehmen in einem anderen EU-Land, verrechnest du in der Regel
              keine österreichische Umsatzsteuer. Die Steuer schuldet dein Kunde in seinem Land (Reverse Charge).
              Dafür brauchst du eine UID-Nummer, auch als Kleinunternehmer. Auf der Rechnung stehen deine UID, die UID
              deines Kunden und der Hinweis auf den Übergang der Steuerschuld. Zusätzlich meldest du diese Umsätze
              regelmäßig in der Zusammenfassenden Meldung (ZM) ans Finanzamt. Bei Privatkunden oder beim Verkauf von
              Waren gelten andere Regeln.
            </p>

            {/* Freiwillig */}
            <h2 id="freiwillig" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wann sich die Umsatzsteuerpflicht freiwillig lohnt
            </h2>
            <p className="mb-8 leading-relaxed">
              Wenn deine Kunden größtenteils Unternehmen sind, die die Vorsteuer ohnehin zurückbekommen, oder wenn
              du höhere Investitionen planst, kann ein freiwilliger Verzicht auf die Kleinunternehmerregelung sich
              lohnen – du bekommst dann die Vorsteuer auf deine Einkäufe zurück. Klingt einfach, aber: der Verzicht
              (Regelbesteuerungsantrag) bindet dich mindestens 5 Kalenderjahre{/* F-V6 */}. Keine Entscheidung für
              aus dem Bauch heraus.
            </p>

            {/* Fehler */}
            <h2 id="fehler" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Fehler, die wir am häufigsten sehen
            </h2>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>Rechnung ohne USt-Ausweis geschrieben, obwohl man gar nicht (mehr) Kleinunternehmer ist</li>
              <li>Vorsteuer ohne ordnungsgemäße Rechnung abgezogen – fällt meist erst bei einer Prüfung auf</li>
              <li>Kleinunternehmergrenze überschritten und erst Monate später reagiert</li>
              <li>
                Reverse Charge bei Rechnungen von Google, Meta oder anderen ausländischen Anbietern
                übersehen{/* F-V9 */}
              </li>
              <li>Freiwillig auf die Kleinunternehmerregelung verzichtet, ohne die 5-Jahres-Bindung einzupreisen</li>
            </ul>

            <p className="mb-10 leading-relaxed">
              <strong>Was heißt das für dich?</strong> Ob Kleinunternehmer-Status oder Umsatzsteuerpflicht zu dir
              passt, hängt von deinen Kunden und Ausgaben ab – und von einer 5-Jahres-Bindung, wenn du dich
              umentscheidest. Das rechnen wir im kostenlosen Erstgespräch gemeinsam durch.{' '}
              {/* TODO: PublicOnly/InternalOnly once T1 ships.
                  InternalOnly: "Fragen dazu? Schreib uns im Portal oder an clients@pstax.at" */}
              <Link to="/de/#fit-check" className="text-pstax-link hover:underline font-semibold">
                Kostenloses Erstgespräch buchen
              </Link>
              .
            </p>

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Häufige Fragen
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_UST_DE.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span aria-hidden="true" className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            <p className="mb-10 leading-relaxed text-sm text-pstax-gray">
              <strong className="text-pstax-navy">Weiterlesen</strong> 2-Minuten-Kurzinfo zu diesem Thema
              <SoonBadge /> · Kleinunternehmerregelung
              <SoonBadge /> · UID-Nummer
              <SoonBadge />
            </p>

            {/* Quellen */}
            <h2 id="quellen" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Quellen
            </h2>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                usp.gv.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/steuersaetze-und-steuerbefreiungen-der-umsatzsteuer.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Umsatzsteuer Überblick – Steuersätze
                </ExternalLink>
                {/* F-V1 */}
              </li>
              <li>
                usp.gv.at / wko.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/weitere-steuertatbestaende-und-befreiungen/kleinunternehmen.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Kleinunternehmerregelung
                </ExternalLink>
                {/* F-V2, F-V3, F-V6 */}
              </li>
              <li>
                usp.gv.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/vorsteuerabzug-und-rechnung/fuer-den-vorsteuerabzug-notwendige-rechnungsmerkmale.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Rechnungsmerkmale für den Vorsteuerabzug
                </ExternalLink>
                {/* F-V7 */}
              </li>
              <li>
                usp.gv.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/entstehen-der-steuerschuld-und-pflichten/umsatzsteuervoranmeldung.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Umsatzsteuervoranmeldung
                </ExternalLink>
                {/* F-V11 */}
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">Unsicher, wo du stehst?</h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Ob Kleinunternehmer-Status oder Umsatzsteuerpflicht für dich passt, hängt von deinen Kunden und
                Ausgaben ab. Im kostenlosen 30-Minuten-Erstgespräch rechnen wir das für dich durch.{' '}
                {/* TODO: PublicOnly/InternalOnly once T1 ships.
                    InternalOnly: "Schon Klient:in? Fragen dazu beantworten wir dir im Portal oder unter
                    clients@pstax.at." */}
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/de/#fit-check" className="btn-accent">
                  Gratis Erstgespräch buchen
                </Link>
              </div>
              <p className="text-xs text-white/50">
                PSTax betreut über 600 Klient:innen in Österreich, Fixpreise ab 800 €/Jahr, auf Deutsch und
                Englisch.
              </p>
            </div>

            {/* Autorenbox */}
            <div id="autor" className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, österreichischer Steuerberater und Gründer von PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
              <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">Pascal Schraml</p>
              <p className="text-sm text-pstax-gray mb-3">Steuerberater, Gründer von PSTax</p>
              <p className="text-sm text-pstax-gray leading-relaxed">
                Berät seit 2023 Selbstständige und Freelancer in Österreich, Betreuung auf Deutsch und Englisch,
                über 600 Klient:innen.
              </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                Allgemeine Information, ersetzt keine individuelle Steuerberatung. Alle Zahlen gelten für 2026 und
                ändern sich jährlich. PSTax (Pascal Schraml Steuerberatung FlexCo) ist Mitglied der Kammer der
                Steuerberater:innen und Wirtschaftsprüfer:innen (KSW).
              </p>
            </div>

            <MoreTopics currentKey={TOPIC.key} lang="de" />
            <FitCheckBox lang="de" />
          </article>
      </div>
    </div>
  );
};
